import { useEffect, useState } from "react";
import Markdown from "react-markdown";
import remarkGfm from "remark-gfm";

import DownloadApp from "@/components/download-app";
import Header from "@/components/header-v2";
import { cn } from "@/lib/utils";

const NON_LETTER_REGEX = /[^a-z]/gi;
const WHITESPACE_REGEX = /\s+/g;

export interface ImageFormatsType {
  ext: string;
  hash: string;
  height: number;
  mime: string;
  name: string;
  path: string;
  size: number;
  url: string;
  width: number;
}

export interface ImageProps {
  data: {
    attributes: {
      formats: {
        large: ImageFormatsType;
        small: ImageFormatsType;
        medium: ImageFormatsType;
        thumbnail: ImageFormatsType;
      };
    };
  };
}

export interface AuthorProps {
  data: {
    attributes: {
      name: string;
      bio: string;
      createdAt: string;
      avatar: {
        data: {
          attributes: {
            formats: {
              thumbnail: ImageFormatsType;
            };
          };
        };
      };
    };
  };
}

export interface BlogPostProps {
  blogs: {
    data: {
      attributes: {
        author: AuthorProps;
        body: string;
        image: ImageProps;
        metaDescription: string;
        publishedAt: string;
        slug: string;
        title: string;
      };
    }[];
  };
  largestImage: { size: number; url: string };
}

interface BlogTextProps {
  body: string;
  title: string;
}

interface AuthorDataProps {
  className?: string;
  image: {
    name: string;
    url: string;
  };
  isBio?: boolean;
  name: string;
  text: string;
}

function AuthorData({ className, name, text, image, isBio }: AuthorDataProps) {
  return (
    <div
      className={cn(
        "mt-5 flex gap-2.5 md:mt-10",
        isBio ? "md:gap-10" : "md:gap-7.5",
        className
      )}
    >
      <img
        alt={name}
        className="h-10 w-10 rounded-[100px] object-cover md:h-25 md:w-25"
        src={image.url}
      />
      <div>
        <p className="font-semibold text-18 md:text-32">{name}</p>
        <div className={cn(isBio && "w-[563px]")}>
          <p
            className={cn(
              "mt-5 text-10 text-pale-lavender leading-[1.875em]",
              isBio ? "md:text-16" : "md:text-20"
            )}
          >
            {text}
          </p>
        </div>
      </div>
    </div>
  );
}

function BlogText({ title, body }: BlogTextProps) {
  return (
    <div>
      <p className="font-semibold text-32">{title}</p>
      <div className="text-body">
        <Markdown remarkPlugins={[remarkGfm]}>{body}</Markdown>
      </div>
    </div>
  );
}

export default function BlogPost({
  blogs: { data },
  largestImage,
}: BlogPostProps) {
  const [tableOfContents, setTableOfContents] = useState<HTMLElement[]>([]);

  const blog = data[0].attributes;
  const author = blog.author.data.attributes;
  const avatar = author.avatar.data.attributes.formats.thumbnail;
  const formatedDate = new Date(blog.publishedAt).toLocaleString("en-US", {
    day: "numeric",
    month: "long",
    year: "numeric",
  });

  useEffect(() => {
    const topics: HTMLElement[] = Array.from(
      document.getElementById("content")?.querySelectorAll("h1,h2") || []
    );
    topics.forEach((topic, index) => {
      topic.setAttribute(
        "id",
        (topic.textContent || `${index}`)
          .replace(NON_LETTER_REGEX, " ")
          .trim()
          .replace(WHITESPACE_REGEX, "_")
      );
    });

    setTableOfContents(topics);
  }, []);

  return (
    <>
      <Header variant="simplified" />
      <section className="container">
        <div className="w-[325px] pt-70 md:mt-200 md:w-280 md:pt-0">
          <h1 className="font-semibold text-28 text-white leading-[1.29em] md:text-60">
            {blog.title}
          </h1>
        </div>
        <AuthorData
          image={avatar}
          name={author.name}
          text={`${formatedDate} - ${Math.floor(blog.body.split(" ").length / 275)} min read`}
        />
        <div className="mt-20 flex flex-col md:flex-row">
          <div className="md:w-[397px]">
            <p className="font-semibold text-18 md:text-28">
              Table of Contents
            </p>
            <ul className="mt-[15px] md:mt-5">
              {tableOfContents?.map((el, i) => (
                // biome-ignore lint/suspicious/noArrayIndexKey: legacy
                <li className="mt-2.5 md:mt-4" key={i}>
                  <a href={`#${el.id}`}>{el.textContent}</a>
                </li>
              ))}
            </ul>
          </div>
          <div className="mt-20 flex flex-col md:mt-0 md:ml-10 md:w-[683px]">
            <BlogText body={blog.body} title={blog.title} />
            <AuthorData
              className="hidden md:flex"
              image={avatar}
              isBio
              name={author.name}
              text={author.bio}
            />
          </div>
        </div>
        <DownloadApp className="my-20 pt-[167px] md:my-35 md:pt-[183px] md:pb-[183px]" />
      </section>
      <div className="absolute inset-x-0 top-0 -z-1 h-66.5 bg-white md:h-224.5">
        <img
          alt={blog.title}
          className="size-full object-cover"
          src={largestImage.url}
        />
      </div>
    </>
  );
}
